import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import type { ConnectionState } from '../services/websocket';

export interface User {
  id: string;
  name: string;
  email: string;
  avatar?: string;
  roles: string[];
}

interface AuthState {
  user: User | null;
  impersonatedUser: User | null;
  /** Read-only delegated history view (manager viewing a staffer's chats). */
  viewAsUser: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  connectionState: ConnectionState;
  /**
   * D6-16: true once the gateway websocket has authenticated at least once
   * this session. Lets the connection-lost banner distinguish "still starting
   * up" (no banner) from "we had a connection and lost it" (persistent banner).
   */
  hasEverConnected: boolean;

  loginWithToken: (token: string, user: User) => void;
  clearSession: () => void;
  logout: () => void;
  setConnectionState: (state: ConnectionState) => void;
  setLoading: (loading: boolean) => void;
  setImpersonation: (user: User) => void;
  clearImpersonation: () => void;
  setViewAs: (user: User) => void;
  clearViewAs: () => void;
}

const noopStorage = {
  getItem: () => null,
  setItem: () => undefined,
  removeItem: () => undefined,
};

const persistStorage = createJSONStorage(() => {
  const storage = typeof window !== 'undefined' ? window.sessionStorage : undefined;

  if (
    storage &&
    typeof storage.getItem === 'function' &&
    typeof storage.setItem === 'function' &&
    typeof storage.removeItem === 'function'
  ) {
    return storage;
  }

  return noopStorage;
});

function disconnectWebSocket() {
  import('../services/websocket')
    .then(({ allyWebSocket }) => {
      allyWebSocket.disconnect();
    })
    .catch(() => {
      // WebSocket service might not be loaded yet.
    });
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      impersonatedUser: null,
      viewAsUser: null,
      token: null,
      isAuthenticated: false,
      isLoading: true,
      connectionState: 'disconnected',
      hasEverConnected: false,

      loginWithToken: (token: string, user: User) => {
        set({
          user,
          token,
          isAuthenticated: true,
          isLoading: false,
        });
      },

      clearSession: () => {
        set({
          user: null,
          impersonatedUser: null,
          viewAsUser: null,
          token: null,
          isAuthenticated: false,
          isLoading: false,
          connectionState: 'disconnected',
          hasEverConnected: false,
        });
        disconnectWebSocket();
      },

      logout: () => {
        set({
          user: null,
          impersonatedUser: null,
          viewAsUser: null,
          token: null,
          isAuthenticated: false,
          isLoading: false,
          connectionState: 'disconnected',
          hasEverConnected: false,
        });
        disconnectWebSocket();
      },

      setConnectionState: (state: ConnectionState) => {
        set((prev) => ({
          connectionState: state,
          hasEverConnected: prev.hasEverConnected || state === 'connected',
        }));
      },

      setLoading: (loading: boolean) => {
        set({ isLoading: loading });
      },

      setImpersonation: (user: User) => {
        set({ impersonatedUser: user });
      },

      clearImpersonation: () => {
        set({ impersonatedUser: null });
      },

      setViewAs: (user: User) => {
        set({ viewAsUser: user });
      },

      clearViewAs: () => {
        set({ viewAsUser: null });
      },
    }),
    {
      name: 'ally-auth-store',
      storage: persistStorage,
      partialize: (state) => ({
        user: state.user,
        impersonatedUser: state.impersonatedUser,
        viewAsUser: state.viewAsUser,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
      }),
    },
  ),
);

export const getAuthHeaders = (options: { includeImpersonation?: boolean } = {}): Record<string, string> => {
  const { token, impersonatedUser } = useAuthStore.getState();
  const headers: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};

  if (options.includeImpersonation) {
    const impersonationKey = impersonatedUser?.email || impersonatedUser?.id;
    if (impersonationKey) {
      headers['X-Ally-Impersonate-User'] = impersonationKey;
    }
  }

  return headers;
};

/** Read-only delegated view target key for `?viewAs=` on conversation GETs. */
export const getViewAsKey = (): string | null => {
  const { viewAsUser } = useAuthStore.getState();
  return viewAsUser?.email || viewAsUser?.id || null;
};

export const hasRole = (role: string): boolean => {
  const { user } = useAuthStore.getState();
  return user?.roles.includes(role) ?? false;
};
